import { ComponentType, FC } from 'react';

import { withAuthState } from './authState';
import { withChakra } from './chakra';
import { withCookies } from './cookies';
import { withEnv } from './env';
import { withFeatureFlags } from './featureFlags';
import { withGlobalLoader } from './globalLoader';
import { withHelmet } from './helmet';
import { withI18Next } from './i18next';
import { withLaunchDarkly } from './launchDarkly';
import { LaunchDarklyContextInitializer } from './launchDarkly/LaunchDarklyContextInitializer';
import { withReturnValue } from './returnValue';
import { withRollbar } from './rollbar';
import { withSuspense } from './suspense';

type HOC = (Component: ComponentType<any>) => ComponentType<any>;

/**
 * Wraps a plain app component with all the middlewares
 * @param MainAppComponent - The main app component to wrap
 * @returns React component wrapped with all middlewares
 */
export const withMiddlewares = <
  P extends Record<string, any> = Record<string, any>,
>(
  MainAppComponent: ComponentType<P>
): FC<P> => {
  return function WrappedWithMiddlewares(props: P) {
    // order matters - applied in reverse order so first element is outer layer
    const middlewares: Array<(comp: ComponentType<any>) => ComponentType<any>> =
      [
        withEnv,
        withHelmet,
        withCookies,
        withSuspense,
        withI18Next,
        withChakra,
        withRollbar,
        withFeatureFlags,
        withGlobalLoader,
        withReturnValue,
        withLaunchDarkly,
        withAuthState,
      ] as Array<(comp: ComponentType<any>) => ComponentType<any>>;

    const WrappedMainApp = [...middlewares]
      .reverse() // ensure ordering
      .reduce<ComponentType<P>>(
        (WrappedApp, withFn) => {
          return withFn(WrappedApp) as ComponentType<P>;
        },
        () => (
          <MainAppComponent {...(props as any)}>
            <LaunchDarklyContextInitializer />
          </MainAppComponent>
        )
      );

    return <WrappedMainApp {...(props as any)} />;
  };
};
